import { type MfaMethod, useMfa } from '@privy-io/react-auth';
import { useCallback } from 'react';

import { useWalletMfaOperationPurpose } from './use-wallet-mfa-operation-purpose';
import {
  useVerificationErrorHandler,
  useVerificationSession,
  type VerificationSession,
} from './use-wallet-mfa-verification-session';
import {
  WalletMfaMethod,
  WalletMfaVerificationPhase,
  WalletMfaVerificationPurpose,
} from './wallet-mfa-types';
import {
  createVerificationState,
  RESTART_AFTER_CONCURRENT_REQUEST,
  RESTART_AFTER_TIMEOUT,
} from './wallet-mfa-verification-state';

type MfaApi = ReturnType<typeof useMfa>;
type OperationPurpose = ReturnType<typeof useWalletMfaOperationPurpose>;
type ShowError = (_error: unknown) => void;

export const useWalletMfaVerificationController = () => {
  const { init, submit, cancel } = useMfa();
  const purpose = useWalletMfaOperationPurpose();
  const session = useVerificationSession(cancel);
  const showError = useVerificationErrorHandler(session);
  const beginTotp = useTotpChallenge(session, init, showError);
  const handleMfaRequired = useMfaRequiredHandler(session, purpose, { cancel, beginTotp });
  const { changeCode, verifyCode } = useTotpCodeEntry(session, submit, showError);
  const { runRef, finish } = session;
  const { cancelPendingSecurityOperation } = purpose;

  const cancelVerification = useCallback(() => {
    const run = runRef.current;

    if (run.active && run.purpose === WalletMfaVerificationPurpose.SecurityChange) {
      cancelPendingSecurityOperation();
    }

    cancel();
    finish();
  }, [cancel, cancelPendingSecurityOperation, finish, runRef]);

  return {
    state: session.state,
    runSecurityOperation: purpose.runSecurityOperation,
    handleMfaRequired,
    changeCode,
    verifyCode,
    restart: finish,
    cancelVerification,
  };
};

const useTotpChallenge = (
  { runRef, setState }: VerificationSession,
  init: MfaApi['init'],
  showError: ShowError,
) =>
  useCallback(async () => {
    const runId = runRef.current.id;
    setState(
      createVerificationState(WalletMfaVerificationPhase.TotpStarting, runRef.current.purpose),
    );

    try {
      await init(WalletMfaMethod.Totp);
    } catch (error) {
      if (runId === runRef.current.id) {
        showError(error);
      }

      return;
    }

    if (runId === runRef.current.id) {
      runRef.current.autoSubmitted = false;
      runRef.current.submitting = false;
      setState(createVerificationState(WalletMfaVerificationPhase.Totp, runRef.current.purpose));
    }
  }, [init, runRef, setState, showError]);

const useChallengeRefusal = (
  { runRef, finish, failClosed }: VerificationSession,
  cancelPendingSecurityOperation: () => void,
  cancel: MfaApi['cancel'],
) => {
  const cancelAfterListenerReturns = useCallback(() => {
    // Privy 3.37 installs an event resolver only after its MFA listener returns.
    queueMicrotask(cancel);
  }, [cancel]);

  const declineChallenge = useCallback(() => {
    if (runRef.current.purpose === WalletMfaVerificationPurpose.SecurityChange) {
      cancelPendingSecurityOperation();
    }

    finish();
    cancelAfterListenerReturns();
  }, [cancelAfterListenerReturns, cancelPendingSecurityOperation, finish, runRef]);

  const rejectConcurrentChallenge = useCallback(() => {
    failClosed(RESTART_AFTER_CONCURRENT_REQUEST);
    cancelAfterListenerReturns();
  }, [cancelAfterListenerReturns, failClosed]);

  return { declineChallenge, rejectConcurrentChallenge };
};

const useMfaRequiredHandler = (
  session: VerificationSession,
  { currentPurpose, cancelPendingSecurityOperation }: OperationPurpose,
  { cancel, beginTotp }: { cancel: MfaApi['cancel']; beginTotp: () => Promise<void> },
) => {
  const { runRef, failClosed, startIdleTimer } = session;
  const { declineChallenge, rejectConcurrentChallenge } = useChallengeRefusal(
    session,
    cancelPendingSecurityOperation,
    cancel,
  );

  return useCallback(
    ({ mfaMethods }: { mfaMethods: MfaMethod[] }, hasInlineHost: boolean) => {
      const run = runRef.current;
      run.purpose = currentPurpose();

      if (!hasInlineHost || !mfaMethods.includes(WalletMfaMethod.Totp)) {
        declineChallenge();
        return;
      }

      if (run.active) {
        rejectConcurrentChallenge();
        return;
      }

      run.active = true;
      run.id += 1;
      startIdleTimer(() => failClosed(RESTART_AFTER_TIMEOUT));
      void beginTotp();
    },
    [
      beginTotp,
      currentPurpose,
      declineChallenge,
      failClosed,
      rejectConcurrentChallenge,
      runRef,
      startIdleTimer,
    ],
  );
};

const useTotpSubmission = (
  { runRef, setState, finish }: VerificationSession,
  submit: MfaApi['submit'],
  showError: ShowError,
) =>
  useCallback(
    (code: string) => {
      if (code.length !== 6 || runRef.current.submitting) {
        return;
      }

      const runId = runRef.current.id;

      const settle = (next: () => void) => {
        if (runId === runRef.current.id) {
          runRef.current.submitting = false;
          next();
        }
      };

      runRef.current.submitting = true;
      setState((current) => ({ ...current, phase: WalletMfaVerificationPhase.TotpSubmitting }));
      void submit(WalletMfaMethod.Totp, code).then(
        () => settle(finish),
        (error) => settle(() => showError(error)),
      );
    },
    [finish, runRef, setState, showError, submit],
  );

const useTotpCodeEntry = (
  session: VerificationSession,
  submit: MfaApi['submit'],
  showError: ShowError,
) => {
  const { state, runRef, setState } = session;
  const submitTotpCode = useTotpSubmission(session, submit, showError);

  const changeCode = useCallback(
    (code: string) => {
      setState((current) => ({ ...current, code, error: null }));

      if (code.length !== 6 || runRef.current.autoSubmitted) {
        return;
      }

      runRef.current.autoSubmitted = true;
      submitTotpCode(code);
    },
    [runRef, setState, submitTotpCode],
  );

  const verifyCode = useCallback(() => {
    if (state.phase === WalletMfaVerificationPhase.Totp) {
      submitTotpCode(state.code);
    }
  }, [state.code, state.phase, submitTotpCode]);

  return { changeCode, verifyCode };
};
